import React from 'react'
import { Button, Columns, Container, Element, Heading, Image, Section } from 'react-bulma-components'
import { BiChevronLeft, BiChevronRight } from 'react-icons/bi'
import Layout from '../components/layout/layout'
import { Translate } from '../utils/translations'
import { OptimizedImage } from '../components/optimized-image'
import { setTranslations } from 'react-i18nify'
import { campaign } from '../campaigns'
import WizardHistory from '../components/form/wizard-history'
import StepRenderer from '../components/form/step-renderer'
import { useWizardHistory } from '../hooks/use-wizard-history'
import { useIsMobile } from '../hooks/use-is-mobile'

export type WizardStep =
  | 'emailConfirmation'
  | 'ownerCheck'
  | 'ownerChanged'
  | 'crankQuantity'
  | 'notConcerned'
  | 'concerned'
  | 'repairKitRequest'
  | 'repairKitRequestSuccess'
  | 'unknownNewOwner'

const FormPage: React.FC = () => {
  const isMobile = useIsMobile()

  // Use the new wizard history hook instead of simple state
  const {
    currentStep,
    goToNextStep,
    goToStep,
    goBack,
    goForward,
    canGoBack,
    canGoForward,
    getCompletedSteps,
    getStepData,
    getStepFormValues,
    setStepFormValues,
  } = useWizardHistory(
    {
      emailConfirmation: 'emailConfirmation',
      ownerCheck: 'ownerCheck',
      ownerChanged: 'ownerChanged',
      crankQuantity: 'crankQuantity',
      notConcerned: 'notConcerned',
      concerned: 'concerned',
      repairKitRequest: 'repairKitRequest',
      repairKitRequestSuccess: 'repairKitRequestSuccess',
      unknownNewOwner: 'unknownNewOwner',
    } as Record<WizardStep, WizardStep>,
    'emailConfirmation',
    // Per-campaign key so a refresh keeps the wizard state within the same tab,
    // without ever mixing snapshots between campaigns.
    `wizard:${campaign.slug}`,
  )

  // Terminal steps: navigation arrows are hidden to avoid re-submitting a
  // completed flow (which would create duplicate records).
  const FINAL_STEPS: WizardStep[] = ['repairKitRequestSuccess', 'unknownNewOwner', 'notConcerned']
  const isFinalStep = FINAL_STEPS.includes(currentStep as WizardStep)

  setTranslations(campaign.formLocales)

  /**
   * Handles step navigation actions from the StepRenderer component
   * Maps action names to their corresponding step transitions
   */
  const handleStepAction = (action: string, data?: any) => {
    switch (action) {
      case 'goToCrankQuantity':
        goToNextStep('crankQuantity', data)
        break
      case 'goToOwnerChanged':
        goToNextStep('ownerChanged', data)
        break
      case 'goToUnknownNewOwner':
        goToNextStep('unknownNewOwner', data)
        break
      case 'goToNotConcerned':
        goToNextStep('notConcerned', data)
        break
      case 'goToConcerned':
        goToNextStep('concerned', data)
        break
      case 'goToRepairKitRequest':
        goToNextStep('repairKitRequest', data)
        break
      case 'goToRepairKitRequestSuccess':
        goToNextStep('repairKitRequestSuccess', data)
        break
      default:
        console.warn(`Unknown action: ${action}`)
    }
  }

  return (
    <Layout>
      <Image src={campaign.logo} style={{ maxWidth: 320, margin: 'auto' }} />
      <Section pb={2} mb={5}>
        <Container>
          <Heading size={2} textAlign="center" mb={6}>
            <Translate value="title" />
          </Heading>
          <Element style={{ maxWidth: 600, margin: 'auto' }} mb={5}>
            <OptimizedImage
              imageName={campaign.productImageName}
              alt={campaign.productImageAlt}
              priority={true}
              loading="eager"
            />
          </Element>
        </Container>

        <Columns centered>
          <Columns.Column size={8}>
            <Element renderAs="p" mt={5} textSize={5} textAlign="center">
              <Translate value="description" />
            </Element>
          </Columns.Column>
          <Columns.Column
            desktop={{ size: 8 }}
            tablet={{ size: 8 }}
            mobile={{ size: 12 }}
            fullhd={{ size: 6 }}
            mt={5}
          >
            {/* Wizard History Component */}
            <WizardHistory
              currentStep={currentStep}
              completedSteps={getCompletedSteps()}
              onStepClick={goToStep as (step: string) => void}
              showHistory={!isMobile}
            />

            {/* Back/forward navigation arrows, sitting between the step history
                and the form box. The right arrow only shows when a next (already
                visited) step exists. Hidden on terminal steps to prevent
                re-submitting a completed flow. */}
            {!isFinalStep && (canGoBack || canGoForward) && (
              <Element
                mb={4}
                style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}
              >
                <Button
                  color="light"
                  onClick={goBack}
                  disabled={!canGoBack}
                  aria-label="Previous step"
                  data-testid="wizard-prev"
                  style={{ visibility: canGoBack ? 'visible' : 'hidden' }}
                >
                  <BiChevronLeft size={22} />
                </Button>
                <Button
                  color="light"
                  onClick={goForward}
                  disabled={!canGoForward}
                  aria-label="Next step"
                  data-testid="wizard-next"
                  style={{ visibility: canGoForward ? 'visible' : 'hidden' }}
                >
                  <BiChevronRight size={22} />
                </Button>
              </Element>
            )}

            <StepRenderer
              currentStep={currentStep}
              onStepSuccess={(stepName, data) => {
                console.log('stepName', stepName, data, '......')
                goToNextStep(stepName as WizardStep, data)
              }}
              onStepAction={handleStepAction}
              getStepData={getStepData}
              getStepFormValues={getStepFormValues}
              setStepFormValues={setStepFormValues}
            />
          </Columns.Column>
        </Columns>
      </Section>
    </Layout>
  )
}

export default FormPage
